import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import * as Analytics from 'analytics/analytics'
import NestableClickable from 'components/Common/NestableClickable'
import { useAppSelector } from 'hooks/reduxHooks'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useMerchandiseOffer from 'hooks/Merchandise/useMerchandiseOffer'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { rem } from 'polished'
import React, {
  useCallback,
  useContext,
  useEffect,
} from 'react'
import styled, { css } from 'styled-components'
import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import MerchandiseLowStockLabel from 'components/Merchandise/MerchandiseLowStockLabel'
import MerchandiseFreeShippingLabel from 'components/Merchandise/MerchandiseFreeShippingLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import Group from 'components/utils/Group'
import PriceRowMerchandiseCardPriceStack from 'LuxKit/PricePoints/Merchandise/PriceRowMerchandiseCardPriceStack'
import MerchandiseCardSkeleton from './MerchandiseCardSkeleton'
import { take } from 'lib/array/arrayUtils'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { useOfferInViewTracking } from 'hooks/useOfferInViewTracking'
import { MerchandiseColourOption } from '../MerchandiseColourOption'
import useMerchandiseColorOptions from 'hooks/Merchandise/useMerchandiseColorOptions'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import LineSearchPlusIcon from 'LuxKit/Icons/line/LineSearchPlusIcon'
import ModalContext from 'contexts/ModalContext'
import MerchandiseQuickPreviewModal from '../MerchandiseQuickPreviewModal'

const Card = styled(NestableClickable)`
  display: grid;
  gap: ${rem(16)};
  grid-template-columns: 1fr;
  grid-template-rows: ${rem(240)} 1fr;
  min-width: 0;

  ${mediaQueryUp.tablet} {
    grid-template-rows: ${rem(270)} 1fr;
  }

  &:focus {
    ${interactiveFocusStyle}
  }
`

const ImageContainer = styled.div`
  position: relative;
  height: 100%;

  > * {
    background-color: ${props => props.theme.palette.neutral.default.eight};
  }

  >:nth-child(2) {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0;
    transition: opacity 0.2s;
  }

  // On hover, we want to show the 2nd image (if there is one)
  ${mediaHoverable} {
    &:hover {
      >:nth-child(2) {
        opacity: 1;
      }
    }
  }
`

const HeaderTileWrapper = styled.div`
  position: relative;

  ${mediaHoverable} {
    &:hover .quick-preview-btn {
      opacity: 1;
    }
  }
`

const TopLeftContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  display: flex;
  gap: ${rem(4)};
  flex-direction: column;
  align-items: stretch;
  width: max-content;

  span {
    width: 100%;
    max-width: 100%;
    display: flex;
    justify-content: center;
  }
`

const QuickPreviewButtonContainer = styled.div`
  position: absolute;
  bottom: ${rem(8)};
  right: ${rem(8)};
  z-index: 2;
  transition: opacity 0.2s;

  ${mediaHoverable} {
    opacity: 0;
  }
`

const darkButtonStyles = css`
  &&& {
    background-color: ${props => props.theme.palette.neutral.default.one};
    color: ${props => props.theme.palette.neutral.default.eight};
    border-color: transparent;

    &:hover {
      background-color: ${props => props.theme.palette.neutral.default.two};
    }

    &:active {
      background-color: ${props => props.theme.palette.neutral.default.three};
    }
  }
`

const PreviewButton = styled(TextButton)`${darkButtonStyles}`
const PreviewIconButton = styled(IconButton)`${darkButtonStyles}`

interface ColourOptionsDisplayProps {
  colorOptions: ReturnType<typeof useMerchandiseColorOptions>
  maxVisible: number
}

function ColourOptionsDisplay({ colorOptions, maxVisible }: ColourOptionsDisplayProps) {
  const { options } = colorOptions

  if (options.length <= 1) return null

  return (
    <Group direction="horizontal" gap={4} verticalAlign="center">
      {take(options, maxVisible).map(option => (
        <MerchandiseColourOption key={option.key} size={12} colors={option.colors} />
      ))}
      {options.length > maxVisible && (
        <BodyText variant="small" weight="bold" colour="neutral-two" wrap="no-wrap">
          +{options.length - maxVisible} more
        </BodyText>
      )}
    </Group>
  )
}

interface Props {
  id: string
  index: number
  prioritisedVariantIds?: Array<string>
}

function MerchandiseOfferCard(props: Props) {
  const { id, index, prioritisedVariantIds } = props
  const [merchandise, fetching, error] = useMerchandiseOffer(id, { prioritisedVariantIds })
  const currency = useAppSelector(state => state.geo.currentCurrency)

  const { setTileRef, trackingInView } = useOfferInViewTracking()
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const showModal = useContext(ModalContext)

  useEffect(() => {
    if (trackingInView && merchandise) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer: merchandise,
        position: index,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingInView])

  const handleClick = useCallback(() => {
    if (merchandise) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: merchandise,
        position: index,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchandise, index])

  const trackPreviewEvent = useCallback((offer: App.MerchandiseOffer) => {
    Analytics.trackClientEvent({
      subject: 'merchandise_preview',
      action: 'opened',
      category: 'shop',
      type: 'interaction',
      context: [
        comluxgroup.createItem_1_1_1({
          offerId: offer.id,
          categoryId: 'shop',
          productId: 'merchandise',
          price: offer.variants[0]?.price,
          currency,
        }),
      ],
    })

    dispatchOfferListEvent({
      type: OfferListEvents.productClick,
      offer,
      position: index,
    })
  }, [currency, dispatchOfferListEvent, index])

  const handleQuickPreview = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (!merchandise) return

    trackPreviewEvent(merchandise)
    showModal(<MerchandiseQuickPreviewModal offer={merchandise} />)
  }, [merchandise, showModal, trackPreviewEvent])

  const colorOptions = useMerchandiseColorOptions(merchandise?.variants)

  if (error) {
    return null
  }

  if (fetching || !merchandise) {
    return <MerchandiseCardSkeleton />
  }

  const [firstImage, secondImage] = merchandise.variants[0].images
  const brandName = merchandise.brand?.name

  return <Card to={`/shop/offer/${merchandise.slug}/${merchandise.id}`} onClick={handleClick}>
    <HeaderTileWrapper>
      <TopLeftContainer>
        <LabelGroup>
          <MerchandiseFreeShippingLabel offer={merchandise} />
        </LabelGroup>
        <LabelGroup>
          <MerchandiseLowStockLabel variants={merchandise.variants} />
        </LabelGroup>
      </TopLeftContainer>

      <QuickPreviewButtonContainer className="quick-preview-btn">
        <CSSBreakpoint max="mobile">
          <PreviewIconButton
            kind="secondary"
            size="medium"
            shape="circle"
            onClick={handleQuickPreview}
            aria-label="Quick preview"
          >
            <LineSearchPlusIcon />
          </PreviewIconButton>
        </CSSBreakpoint>
        <CSSBreakpoint min="tablet">
          <PreviewButton
            kind="secondary"
            size="small"
            shape="round"
            startIcon={<LineSearchPlusIcon />}
            onClick={handleQuickPreview}
          >
            Preview
          </PreviewButton>
        </CSSBreakpoint>
      </QuickPreviewButtonContainer>

      <ImageContainer ref={setTileRef}>
        <Image
          image={firstImage}
          fit="center"
          objectFit="contain"
          width={270}
        />
        {secondImage && <Image
          image={secondImage}
          fit="center"
          objectFit="contain"
          width={270}
        />}
      </ImageContainer>
    </HeaderTileWrapper>
    <Group direction="vertical" verticalAlign="space-between" gap={16}>
      <VerticalSpacer gap={8}>
        <div>
          {brandName && <BodyText variant="small" wrap="truncate">{brandName}</BodyText>}
          <BodyText variant="large" weight="bold" lineClamp={2}>
            {merchandise.name}
          </BodyText>
        </div>

        {colorOptions.options.length > 1 && <>
          <CSSBreakpoint max="mobile">
            <ColourOptionsDisplay colorOptions={colorOptions} maxVisible={5} />
          </CSSBreakpoint>
          <CSSBreakpoint min="tablet" max="tablet">
            <ColourOptionsDisplay colorOptions={colorOptions} maxVisible={7} />
          </CSSBreakpoint>
          <CSSBreakpoint min="desktop">
            <ColourOptionsDisplay colorOptions={colorOptions} maxVisible={8} />
          </CSSBreakpoint>
        </>}
      </VerticalSpacer>

      <CSSBreakpoint min="tablet">
        <PriceRowMerchandiseCardPriceStack
          price={merchandise.variants[0].price}
          regularPrice={merchandise.variants[0].regularPrice}
          points={merchandise.variants[0].pointsPrice}
          pointsRegularPrice={merchandise.variants[0].pointsRegularPrice}
          variant={merchandise.variants[0]}
          size="L"
          direction="stack"
        />
      </CSSBreakpoint>
      <CSSBreakpoint max="mobile">
        <PriceRowMerchandiseCardPriceStack
          price={merchandise.variants[0].price}
          regularPrice={merchandise.variants[0].regularPrice}
          points={merchandise.variants[0].pointsPrice}
          pointsRegularPrice={merchandise.variants[0].pointsRegularPrice}
          variant={merchandise.variants[0]}
          size="L"
          direction="stack"
        />
      </CSSBreakpoint>
    </Group>
  </Card>
}

export default MerchandiseOfferCard
